"use client"
import React, { useState, useEffect } from 'react'
import Image from "next/image";
import { discardHTMLTags, getBaseImageUrl, getCurrencySymbol, getFinalDiscountTag, getMerchantHref, getRandomStoreSeoTitle } from '@/constants/hooks';
import { OffersOffer } from '@/services/dataTypes';
import OfferOutUrl from './Theme5OfferLink';
import Link from 'next/link';
import OfferModalTrigger from './OfferModalTrigger';

interface Props {
    offer: OffersOffer;
    mer_slug_type: string;
    mer_slug: string;
    type?: string;
    productDetailUrl?: string | null;
    domain: string;
    companyId: string;
}

const OffersCardClient = ({ offer, mer_slug_type, mer_slug, type, productDetailUrl, domain }: Props) => {
    const merchantHref = getMerchantHref(offer.merchant, mer_slug, mer_slug_type);
    const product = offer?.offer || offer;

    const rawProductImg = product?.product_image || (product as any)?.image || (offer as any)?.product_image || (offer as any)?.image;
    const rawMerchantLogo = offer?.merchant?.merchant_logo || (product as any)?.merchant?.merchant_logo;

    const primaryImage = rawProductImg ? getBaseImageUrl(domain, rawProductImg, "") : "";
    const fallbackLogo = rawMerchantLogo ? getBaseImageUrl(domain, rawMerchantLogo, "") : "";

    const [imgSrc, setImgSrc] = useState<string>(primaryImage || fallbackLogo);

    useEffect(() => {
        setImgSrc(primaryImage || fallbackLogo);
    }, [primaryImage, fallbackLogo]);

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0;
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0;
    const discountPercent = originalPrice > 0 && salePrice > 0
        ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
        : null;

    const finalDiscountTag = getFinalDiscountTag(
        product?.offer_title || product?.offer_detail,
        discountPercent,
    );

    const endDate = product?.end_date;

    // daysLeft client-only
    const [daysLeft, setDaysLeft] = useState<number | null>(null)
    useEffect(() => {
        if (endDate) {
            const days = Math.ceil((new Date(endDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
            setDaysLeft(days)
        }
    }, [endDate])

    // storeSeoTitle client-only
    const [storeSeoTitle, setStoreSeoTitle] = useState<string>(offer?.merchant?.merchant_name || "")
    useEffect(() => {
        const name = offer?.merchant?.merchant_name || ""
        if (!name) return
        const phrases = [
            `${name} Coupon Codes & Deals`,
            `${name} Discount Offers`,
            `${name} Discount Deals`,
            `${name} Deals`,
            `${name} Promo Codes`,
            `Exclusive ${name} Discounts`,
            `${name} Sale Events`,
            `Save Big at ${name}`,
            `Top ${name} Coupons & Promos`,
            `${name} Vouchers & Offers`
        ]
        const seed = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
        setStoreSeoTitle(phrases[seed % phrases.length])
    }, [offer?.merchant?.merchant_name])


    return (
        <div className="w-full h-full">
            <div className="group relative bg-white rounded-[2rem] p-6 border border-slate-100 hover:border-indigo-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(79,70,229,0.12)] hover:-translate-y-1.5 transition-all duration-500 flex flex-col justify-between h-full overflow-hidden select-none">

                {/* Subtle gradient hover bg */}
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/0 to-indigo-50/0 group-hover:from-indigo-50/50 group-hover:to-white transition-colors duration-500 -z-10" />

                {/* Top Section */}
                <div className="flex-1 flex flex-col">
                    {/* Top badges */}
                    <div className="flex items-center justify-between gap-1.5 mb-4 w-full min-w-0 flex-wrap sm:flex-nowrap">
                        {(type === "product" || finalDiscountTag) ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-black bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0 max-w-[80%] truncate">
                                {finalDiscountTag}
                            </span>
                        ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-black uppercase tracking-widest bg-emerald-50 text-emerald-600 border border-emerald-100 shrink-0">
                                VERIFIED
                            </span>
                        )}
                        {daysLeft !== null && daysLeft > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[8px] sm:text-[10px] font-black text-amber-600 bg-amber-50/80 border border-amber-100/50 shrink-0 whitespace-nowrap ml-auto">
                                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                {daysLeft} days left
                            </span>
                        )}
                    </div>

                    {/* Image area inside brand container */}
                    <div className="relative w-32 h-32 bg-white rounded-3xl p-2 shadow-sm border border-slate-50 group-hover:scale-105 transition-transform duration-500 flex items-center justify-center mx-auto mb-4 z-10">
                        <Image
                            src={imgSrc || fallbackLogo}
                            alt={discardHTMLTags(product?.offer_title) || 'Deal'}
                            width={128}
                            height={128}
                            className="object-contain w-full h-full p-2"
                            onError={() => {
                                if (fallbackLogo && imgSrc !== fallbackLogo) {
                                    setImgSrc(fallbackLogo);
                                }
                            }}
                        />
                    </div>

                    {/* Product pricing */}
                    {type === "product" && (product?.sale_price || product?.original_price) && (
                        <div className="flex items-baseline justify-center gap-1.5 mb-3 leading-none">
                            {product?.sale_price && (
                                <span className="text-base font-black text-indigo-600">
                                    {getCurrencySymbol(product?.currency)}{product?.sale_price}
                                </span>
                            )}
                            {product?.original_price && (
                                <span className="text-xs text-slate-400 line-through font-medium">
                                    {getCurrencySymbol(product?.currency)}{product?.original_price}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Title Container */}
                    <div className="flex-grow flex flex-col justify-center my-2 min-h-[2.5rem]">
                        {product?.is_detail === 1 && productDetailUrl ? (
                            <Link href={productDetailUrl}>
                                <p className="text-[13px] font-bold text-slate-800 hover:text-indigo-600 transition-colors duration-300 text-center leading-snug line-clamp-2">
                                    {discardHTMLTags(product?.offer_title)}
                                </p>
                            </Link>
                        ) : (
                            <OfferOutUrl
                                unique_id={product?.unique_id}
                                outUrl={product?.url}
                                merchantHref={merchantHref}
                                domain={domain}
                                customClass="block"
                            >
                                <p className="text-[13px] font-bold text-slate-800 hover:text-indigo-600 transition-colors duration-300 text-center leading-snug line-clamp-2">
                                    {discardHTMLTags(product?.offer_title)}
                                </p>
                            </OfferOutUrl>
                        )}
                    </div>

                    {/* View Details — above divider */}
                    <div className="flex items-center justify-end mt-auto pt-1">
                        <OfferModalTrigger
                            product={product}
                            merchantName={offer?.merchant?.merchant_name}
                            merchantLogo={getBaseImageUrl(domain, offer?.merchant?.merchant_logo, "")}
                            domain={domain}
                            className="text-[11px] font-bold text-slate-900 hover:text-indigo-600 transition-colors tracking-wider"
                        >
                            View Details
                        </OfferModalTrigger>
                    </div>

                    {/* Divider */}
                    <div className="w-full border-t border-dashed border-slate-200/80 mt-2"></div>
                </div>

                {/* CTA Action */}
                <div className="mt-auto">
                    {product?.is_detail === 1 && productDetailUrl ? (
                        <Link
                            href={productDetailUrl}
                            className="w-full py-3 flex items-center justify-center text-white text-[11px] font-bold tracking-wider rounded-xl transition-all duration-300 bg-slate-900 hover:bg-indigo-600 shadow-md hover:shadow-indigo-500/20 active:scale-95"
                        >
                            {type === "product" ? "Buy Now" : "Grab Deal"}
                        </Link>
                    ) : (
                        <OfferOutUrl
                            unique_id={product?.unique_id}
                            outUrl={product?.url}
                            merchantHref={merchantHref}
                            domain={domain}
                            customClass="w-full py-3 flex items-center justify-center text-white text-[11px] font-bold tracking-wider rounded-xl transition-all duration-300 bg-slate-900 hover:bg-indigo-600 shadow-md hover:shadow-indigo-500/20 active:scale-95"
                        >
                            {type === "product" ? "Buy Now" : "Grab Deal"}
                        </OfferOutUrl>
                    )}
                </div>
            </div>
        </div>
    );
}

export default OffersCardClient